import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { EmptyContent } from "shared/components/states/EmptyContent";
import { ErrorContent } from "shared/components/states/ErrorContent";
import { LoadingContent } from "shared/components/states/LoadingContent";

export type SectionStateContentSize = "sm" | "md" | "lg";

export const toFetchStatus = (
  isLoading: boolean,
  isError: boolean,
): "error" | "success" | "pending" => {
  if (isError) return "error";
  if (isLoading) return "pending";
  return "success";
};

export const isSuccessState = (state?: SectionState) => {
  if (!state) {
    return true;
  }

  if (state.fetchStatus !== "success") {
    return false;
  }

  return !state.isEmpty;
};

export type SectionState = {
  fetchStatus?: "error" | "success" | "pending";
  /**
   * Used to show the empty state, typically when the fetch status is "success" but there is no data available.
   */
  isEmpty?: boolean;
  onRetry?: () => void;
  emptyReason?: string;
};

type SectionStateContentProps = {
  state?: SectionState;
  size?: SectionStateContentSize;
  children: ReactNode;
};

export const SectionStateContent = ({
  state = { fetchStatus: "success" },
  size = "md",
  children,
}: SectionStateContentProps) => {
  const { t } = useTranslation();

  if (!state) return children;

  if (state.fetchStatus === "pending") return <LoadingContent size={size} />;

  if (state.fetchStatus === "error")
    return <ErrorContent size={size} onRetry={state.onRetry} />;

  if (state.isEmpty)
    return (
      <EmptyContent
        size={size}
        reason={
          state.emptyReason ?? t("Oisann! Her mangler det data for hendelsen.")
        }
      />
    );

  return children;
};
